import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Phone, Mail, MapPin, Instagram, MessageCircle } from "lucide-react";
import { useSettings, waLink, telLink, SITE_ORIGIN } from "@/lib/site";
import { getDistricts, mediaUrl } from "@/lib/api";
import { BrandLogo } from "@/components/layout/BrandLogo";

export const Footer = () => {
  const settings = useSettings();
  const { data: districts = [] } = useQuery({ queryKey: ["districts"], queryFn: getDistricts });
  const year = new Date().getFullYear();

  const localBusiness = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_ORIGIN}/#business`,
    name: settings.brand_name,
    image: settings.hero_image ? mediaUrl(settings.hero_image) : (settings.gallery && settings.gallery[0] ? mediaUrl(settings.gallery[0]) : "https://images.unsplash.com/photo-1571753217087-980e556e16ea?w=1200"),
    logo: settings.logo_url ? mediaUrl(settings.logo_url) : undefined,
    url: SITE_ORIGIN,
    telephone: settings.phone,
    email: settings.email,
    priceRange: "₺₺₺",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Ankara",
      addressCountry: "TR",
    },
    areaServed: districts.map((d) => ({ "@type": "City", name: `${d.name}, Ankara` })),
    sameAs: [settings.instagram],
  };

  return (
    <footer className="relative z-10 bg-[#0B0C0D] border-t border-white/10 pt-16 pb-28 lg:pb-12">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusiness) }} />
      <div className="container-lux grid gap-10 md:grid-cols-4">
        <div className="md:col-span-1">
          <BrandLogo settings={settings} size="lg" idPrefix="footer" />
          <p className="text-sm text-[#9A9387] mt-3 leading-relaxed">
            Ankara ve tüm ilçelerinde gelin damat dış çekim, düğün günü fotoğraf ve video hizmeti.
            Zamansız, şık ve sinematik kareler.
          </p>
          <div className="flex gap-3 mt-5">
            <a href={waLink(settings)} target="_blank" rel="noreferrer" aria-label="WhatsApp"
              className="w-10 h-10 grid place-items-center rounded-full border border-white/15 text-[#F7F2E8] hover:border-[#C8A24A] hover:text-[#C8A24A] transition-colors">
              <MessageCircle className="w-5 h-5" />
            </a>
            <a href={settings.instagram} target="_blank" rel="noreferrer" aria-label="Instagram"
              className="w-10 h-10 grid place-items-center rounded-full border border-white/15 text-[#F7F2E8] hover:border-[#C8A24A] hover:text-[#C8A24A] transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
          </div>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-[0.18em] text-[#C8A24A] mb-4">Sayfalar</h4>
          <ul className="space-y-2.5 text-sm text-[#B8B1A5]">
            <li><Link to="/paketler" className="hover:text-white">Paketler</Link></li>
            <li><Link to="/galeri" className="hover:text-white">Galeri</Link></li>
            <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
            <li><Link to="/iletisim" className="hover:text-white">İletişim</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-[0.18em] text-[#C8A24A] mb-4">Popüler İlçeler</h4>
          <ul className="space-y-2.5 text-sm text-[#B8B1A5]">
            {districts.slice(0, 6).map((d) => (
              <li key={d.slug}>
                <Link to={`/ankara/${d.page_slug}`} className="hover:text-white">
                  {d.name} Düğün Fotoğrafçısı
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-[0.18em] text-[#C8A24A] mb-4">İletişim</h4>
          <ul className="space-y-3 text-sm text-[#B8B1A5]">
            <li><a href={telLink(settings)} className="flex items-center gap-2 hover:text-white"><Phone className="w-4 h-4 text-[#C8A24A]" /> {settings.phone}</a></li>
            <li><a href={`mailto:${settings.email}`} className="flex items-center gap-2 hover:text-white"><Mail className="w-4 h-4 text-[#C8A24A]" /> {settings.email}</a></li>
            <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#C8A24A]" /> {settings.address}</li>
          </ul>
        </div>
      </div>

      <div className="container-lux mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-[#7C766B]">© {year} {settings.brand_name}. Tüm hakları saklıdır.</p>
        <p className="text-xs text-[#7C766B]">Ankara'nın 25 ilçesine düğün fotoğrafçılığı hizmeti.</p>
      </div>
    </footer>
  );
};

export default Footer;
